// Helper utilities for emission category mapping and formatting

export type EmissionCategory = 'energy' | 'transportation' | 'waste';

export interface CategoryData {
  category: EmissionCategory;
  totalEmissions: number;
  measuredCount: number;
  totalCount: number;
  reportingPercentage: number;
}

// Activity type to category mapping
const ACTIVITY_CATEGORY_MAP: Record<string, EmissionCategory> = {
  // Energy category
  'electricity_consumption': 'energy',
  'natural_gas_consumption': 'energy',
  'Grid': 'energy',
  'Solar': 'energy',
  'Coal': 'energy',
  'LPG': 'energy',
  'LPG (Liquefied Petroleum Gas)': 'energy',
  'Diesel': 'energy',
  'fuel_consumption': 'energy',
  'fuel_energy_activities': 'energy',
  'refrigerants': 'energy',
  'fire_suppression': 'energy',
  
  // Transportation category
  'vehicle_emissions': 'transportation',
  'business_travel': 'transportation',
  'transportation': 'transportation',
  'upstream_transportation': 'transportation',
  'Employee Commuting': 'transportation',
  
  // Waste category
  'waste_disposal': 'waste',
  'waste_generated': 'waste',
  'Waste': 'waste',
  'water_consumption': 'waste',
  
  // Default to waste for unknown types
  'other': 'waste',
  'purchased_goods': 'waste',
  'capital_goods': 'waste'
};

// Category display names
export const CATEGORY_DISPLAY_NAMES: Record<EmissionCategory, string> = {
  energy: 'Energy Consumption',
  transportation: 'Transportation',
  waste: 'Waste'
};

// Category colors for charts and UI
export const CATEGORY_COLORS: Record<EmissionCategory, string> = {
  energy: '#22c55e',      // Green 500
  transportation: '#16a34a', // Green 600
  waste: '#15803d'        // Green 700
};

// Category background colors for cards
export const CATEGORY_BG_COLORS: Record<EmissionCategory, string> = {
  energy: 'bg-green-100',
  transportation: 'bg-green-200',
  waste: 'bg-green-300'
};

// Category text colors
export const CATEGORY_TEXT_COLORS: Record<EmissionCategory, string> = {
  energy: 'text-green-800',
  transportation: 'text-green-800',
  waste: 'text-green-900'
};

/**
 * Maps an activity type to its corresponding emission category
 */
export function mapActivityTypeToCategory(activityType: string): EmissionCategory {
  return ACTIVITY_CATEGORY_MAP[activityType] || 'waste';
}

/**
 * Gets the display color for a category
 */
export function getCategoryColor(category: EmissionCategory): string {
  return CATEGORY_COLORS[category];
}

/**
 * Gets the background color class for a category
 */
export function getCategoryBgColor(category: EmissionCategory): string {
  return CATEGORY_BG_COLORS[category];
}

/**
 * Gets the text color class for a category
 */
export function getCategoryTextColor(category: EmissionCategory): string {
  return CATEGORY_TEXT_COLORS[category];
}

/**
 * Converts kg CO2e to MT CO2e (divide by 1000)
 */
export function formatToMTCO2e(kgCO2e: number): string {
  const mtCO2e = kgCO2e / 1000;
  return mtCO2e.toFixed(2);
}

/**
 * Formats a number with commas for better readability
 */
export function formatNumberWithCommas(num: number): string {
  return num.toLocaleString();
}

/**
 * Calculates reporting percentage from measured and total counts
 */
export function calculateReportingPercentage(measuredCount: number, totalCount: number): number {
  if (totalCount === 0) return 0;
  return Math.round((measuredCount / totalCount) * 100);
}

/**
 * Gets the appropriate color class for reporting percentage
 */
export function getReportingPercentageColor(percentage: number): string {
  if (percentage >= 100) return 'text-green-600';
  if (percentage >= 80) return 'text-green-500';
  if (percentage >= 60) return 'text-orange-500';
  return 'text-red-500';
}

/**
 * Groups emission entries by category
 */
export function groupEmissionsByCategory(emissions: any[]): Record<EmissionCategory, any[]> {
  const grouped: Record<EmissionCategory, any[]> = {
    energy: [],
    transportation: [],
    waste: []
  };

  emissions.forEach(emission => {
    const category = mapActivityTypeToCategory(emission.activity_type);
    grouped[category].push(emission);
  });

  return grouped;
}

/**
 * Calculates category data from grouped emissions
 */
export function calculateCategoryData(groupedEmissions: Record<EmissionCategory, any[]>): Record<EmissionCategory, CategoryData> {
  const result: Record<EmissionCategory, CategoryData> = {} as any;

  Object.entries(groupedEmissions).forEach(([category, emissions]) => {
    const totalEmissions = emissions.reduce((sum, emission) => sum + (emission.co2_equivalent || 0), 0);
    const measuredCount = emissions.filter(emission => !emission.is_estimated).length;
    const totalCount = emissions.length;
    const reportingPercentage = calculateReportingPercentage(measuredCount, totalCount);

    result[category as EmissionCategory] = {
      category: category as EmissionCategory,
      totalEmissions,
      measuredCount,
      totalCount,
      reportingPercentage
    };
  });

  return result;
}













